import React, { useState, useRef } from 'react';
import '../css/textArea.css';

export interface TextAreaProps {
  id?: string;
  name?: string;
  value?: string;
  label?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  rows?: number;         // fallback height via row count
  height?: number;       // explicit height in px — takes priority over rows
  maxLength?: number;    // enforces character limit + shows counter
  assistiveText?: string;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
  warningText?: string;
}

const LINE_HEIGHT = 22;       // matches CSS line-height
const PADDING_V = 18;       // wrapper: 10px top + 8px bottom
const FOOTER_H = 22;       // char count footer height

const TextArea: React.FC<TextAreaProps> = ({
  id,
  name,
  value = '',
  label,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  required = false,
  disabled = false,
  className = '',
  rows = 3,
  height,
  maxLength,
  assistiveText,
  error = false,
  errorText,
  warning = false,
  warningText,
}) => { //NOSONAR
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const displayLabel = label || placeholder;
  const hasValue = value && value.length > 0;
  const showFloating = isFocused || hasValue;
  const isAtLimit = maxLength !== undefined && value.length >= maxLength;

  const hasAsterisk = displayLabel?.includes('*');
  const labelText = hasAsterisk
    ? displayLabel?.replace(' *', '').replace('*', '')
    : displayLabel;

  // ── Height calculation ──────────────────────────────────────────────────────
  // Priority: explicit `height` prop → rows × line-height
  const footerExtra = maxLength !== undefined ? FOOTER_H : 0; //NOSONAR
  const innerH = height ?? rows * LINE_HEIGHT;          // textarea pixel height
  const plateMinH = innerH + PADDING_V + footerExtra;      // plate total height

  const renderLabel = () => {
    /* istanbul ignore next */
    if (!displayLabel) return null;
    return (
      <>
        {labelText}
        {(hasAsterisk || required) && (
          <span className="scl--cms-components-textArea-requiredAsterisk"> *</span>
        )}
      </>
    );
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => { setIsFocused(true); onFocus?.(e); };
  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => { setIsFocused(false); onBlur?.(e); };
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => onChange?.(e.target.value);
  const handleClick = () => textareaRef.current?.focus();

  const assistiveMessage =
    error && errorText ? errorText :
      warning && warningText ? warningText : //NOSONAR
        assistiveText;

  const assistiveClass =
    error && errorText ? 'scl--cms-components-textArea-assistive--error' :
      warning && warningText ? 'scl--cms-components-textArea-assistive--warning' : //NOSONAR
        '';

  const plateClasses = [
    'scl--cms-components-textArea-plate',
    isFocused ? 'scl--cms-components-textArea--focused' : '',
    error ? 'scl--cms-components-textArea--error' : '',
    warning && !error ? 'scl--cms-components-textArea--warning' : '',
    disabled ? 'scl--cms-components-textArea--disabled' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`scl--cms-components-textArea-container ${className}`}>
      <div //NOSONAR
        className={plateClasses}
        style={{ minHeight: `${plateMinH}px` }}
        onClick={handleClick}
      >
        {displayLabel && (
          <label
            className={`scl--cms-components-textArea-label ${showFloating ? 'scl--cms-components-textArea-label--floating' : ''
              }`}
          >
            {renderLabel()}
          </label>
        )}

        <div className="scl--cms-components-textArea-wrapper">
          <textarea
            ref={textareaRef}
            id={id}
            name={name}
            value={value}
            maxLength={maxLength}
            disabled={disabled}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={{ height: `${innerH}px` }}
            className="scl--cms-components-textArea-input"
            autoComplete="off"
          />
        </div>

        {maxLength !== undefined && (
          <div className="scl--cms-components-textArea-footer">
            <span
              className={`scl--cms-components-textArea-charCount ${isAtLimit ? 'scl--cms-components-textArea-charCount--limit' : ''
                }`}
            >
              {value.length}/{maxLength}
            </span>
          </div>
        )}
      </div>

      {assistiveMessage && (
        <div className={`scl--cms-components-textArea-assistive ${assistiveClass}`}>
          {assistiveMessage}
        </div>
      )}
    </div>
  );
};

export default TextArea;